import { useMemo } from 'react';

import type { TagModel } from '@snowit/types/tags';

import useFirebase from '@snowit/firebase';
import { SchemaNames } from '@snowit/jsonschemas/__names';
import { useStore } from '@snowit/store';
import omitter from '@snowit/utilities/omitter';

import useSchemas from '../useSchemas';
import useTenants from '../useTenants';
import { isValidTagId } from './utils';

export type CreateTagError =
  | 'tag_id_invalid'
  | 'tag_id_exists'
  | 'tag_id_check_failed'
  | 'tag_write_failed';

export type CreateTagResult = { id: string; error?: never } | { id?: never; error: CreateTagError };

export default () => {
  const firebase = useFirebase();
  const { activeTenants, hasTenants } = useTenants();
  const { store, setStore } = useStore();
  const { writeDocument, deleteDocument } = useSchemas();

  const tags = useMemo(() => {
    return store.tags.list
      .filter((tag) => {
        if (!activeTenants.length) return true;
        return hasTenants(tag.tenants);
      })
      .sort((a, b) => a.uid.localeCompare(b.uid));
  }, [store.tags.list, activeTenants]);

  const getTags = async (): Promise<TagModel[]> => {
    try {
      const res = await firebase.query<TagModel>({
        collectionName: 'tags',
        filters: (q) => q.limit(0),
      });
      setStore('tags', { list: res });
      return res;
    } catch (error) {
      console.error('tags_get', { error });
      return [];
    } finally {
      setStore('tags', { ready: true });
    }
  };

  /**
   * The tag id is chosen by the user, and saving with an existing id would
   * overwrite that tag: check both the loaded list and Firestore.
   * Throws when Firestore cannot be read, since the tag may then exist.
   */
  const tagExists = async (tagUID: string): Promise<boolean> => {
    if (store.tags.list.some((tag) => tag.uid === tagUID)) return true;
    const doc = await firebase.doc<TagModel>({
      collectionName: 'tags',
      id: tagUID,
      throwOnError: true,
    });
    return Boolean(doc);
  };

  const createTag = async (
    tagUID: string | undefined,
    data: Partial<TagModel>,
  ): Promise<CreateTagResult> => {
    if (!isValidTagId(tagUID)) return { error: 'tag_id_invalid' };
    try {
      if (await tagExists(tagUID)) return { error: 'tag_id_exists' };
    } catch (error) {
      console.error('createTag', { error });
      return { error: 'tag_id_check_failed' };
    }
    try {
      const id = await writeDocument({
        modelName: SchemaNames.TAG,
        attributes: omitter(data),
        id: tagUID,
      });
      if (!id) return { error: 'tag_write_failed' };
      await getTags();
      return { id };
    } catch (error) {
      console.error('createTag', { error });
      return { error: 'tag_write_failed' };
    }
  };

  /** The id of an existing tag cannot change: only its attributes are updated. */
  const updateTag = async (
    tagUID: string,
    data: Partial<TagModel>,
  ): Promise<string | undefined> => {
    try {
      const id = await writeDocument({
        modelName: SchemaNames.TAG,
        attributes: omitter(data),
        id: tagUID,
      });
      await getTags();
      return id;
    } catch (error) {
      console.error('updateTag', { error });
      return undefined;
    }
  };

  const deleteTag = async (tagUID: string): Promise<boolean> => {
    try {
      await deleteDocument({
        modelName: SchemaNames.TAG,
        id: tagUID,
      });
      await getTags();
      return true;
    } catch (error) {
      console.error('deleteTag', { error });
      return false;
    }
  };

  return {
    tags,
    ready: store.tags.ready,
    getTags,
    tagExists,
    createTag,
    updateTag,
    deleteTag,
  };
};
